/**
 * Authors: mariushirjanu
 * Created: 21/01/2021
 * Migrated from: message.service.ts
 */
import { Inject, Injectable, Type } from '@angular/core';
import _ from 'underscore';
import * as Noty from 'noty';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { util } from '../util';
import { TranslationService } from '../translation/translation.service';
import { Ga1DialogComponent } from './dialogs/ga1-dialog.component';
import { AnalyticsService } from '../services/analytics.service';
import { DefaultDialogWrapperComponent } from './dialogs/default-dialog-wrapper.component';
import { IExtendedWindow, IObject } from '@type_definitions/datatypes';
import { NoDialogWrapperComponent } from './dialogs/no-dialog-wrapper.component';

export type DialogBusyButtonFn = (busy:boolean) => void;
export type DialogClickFn = (updateBusyButtonFn?:DialogBusyButtonFn) => void;

export interface IDialogButton {
  id?:string;
  label:string;
  className?:string;
  busy?:boolean;
  disabled?:boolean;
  onClick?:DialogClickFn;
  iconClass?:string;
}

export interface IDialogExtraOptions extends Partial<IDialogOptionsInitialState> {
  component?:Type<any>; // the name of the component to load
  inputs?:IObject;
  outputs?:IObject;
  wrapperComponent?:Type<any> | false;  // as default any dialog uses DefaultDialogWrapperComponent; it can be provided another component as a wrapper or set to 'false' to prevent applying the default wrapper
  // ---
  callback?:Function;
  size?:DialogSize;
  closeCallback?:Function;
  hideHeader?:boolean;
  hideFooter?:boolean;
}

export interface IFormErrors {
  title:string;
  errors:string[];
}

interface IDialogOptionsInitialState extends Object, IDialogExtraOptionsInitialState {
  title:string;
  windowClass:string;
  dialogClass:string;
  closeButton:boolean;
  buttons:IDialogButton[];
  manualClose:boolean;
  message:string;
}

interface IDialogExtraOptionsInitialState {
  url:string;
}

export interface IDialogOptions extends ModalOptions, IDialogExtraOptions {
  initialState?:Partial<IDialogOptionsInitialState>;
}

export type IDialog = BsModalRef;

export enum DialogSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  FULL = 'full'
}

export enum MessageType {
  SUCCESS = 'success',
  WARNING = 'warning',
  INFO = 'info',
  DANGER = 'error'
}

@Injectable()
export class GaMessageService {
  private modalDialogs:IDialog[] = [];

  constructor(
    private analyticsService:AnalyticsService,
    private modalService:BsModalService,
    private translationService:TranslationService,
    @Inject(Window) private window:IExtendedWindow
  ) {
    this.window.gaMessageOptions = _.extend(this.window.gaMessageOptions || {}, {
      defaultHideDuration: null,
      defaultAutoHide: false
    });
  }

  private get autoHideDialogDuration():number {
    return this.window.gaMessageOptions?.defaultHideDuration || 5000;
  }

  // ------------- STATUS MESSAGES --------------------------

  public showStatusMessage(msg:string, duration?:number, type?:Noty.Type):Noty {
    const n = new Noty({
      type: type || MessageType.INFO,
      text: this.translationService.instant(msg),
      closeWith: [
        'button',
        'click'
      ]
    });
    n.show();

    // autoclose the status message after the duration
    if (duration) {
      setTimeout(() => {
        this.closeMessage(n);
      }, duration);
    }
    return n;
  }

  public showFeedbackMessage(msg:string, type:Noty.Type = MessageType.SUCCESS):Noty {
    return this.showStatusMessage(msg, this.autoHideDialogDuration, type);
  }

  public showFeedbackAfterSave(label:string):Noty {
    return this.showStatusMessage(this.translationService.instant('GA.GENERAL.STATUS_MESSAGE_SAVED', { label }), this.autoHideDialogDuration, MessageType.SUCCESS);
  }

  public showErrorMessage(msg:string, autoHide?:boolean):Noty {
    console.error(msg);
    // Google Analytics
    this.analyticsService.ga('send', 'exception', {
      page: window.location.href,
      'exDescription': msg
    });

    const isAutoHide:number = this.window.gaMessageOptions?.defaultAutoHide ?  this.autoHideDialogDuration : (autoHide? this.autoHideDialogDuration : NaN);

    return this.showStatusMessage(msg, isAutoHide, MessageType.DANGER);
  }

  public closeMessage(msgNotification:Noty):void {
    if (util.notNullOrUndefined(msgNotification)) {
      msgNotification.close();
    }
  }

  // ------------- DIALOGS --------------------------

  // the dialog can be loaded in 3 main ways:
  // 1) WITH THE DEFAULT WRAPPER ('wrapperComponent' = undefined): DefaultDialogWrapperComponent is used as wrapper
  // 2) WITHOUT WRAPPER ('wrapperComponent' = false): 'component' should have the full content of the dialog
  // 3) WITH CUSTOM WRAPPER ('wrapperComponent' contains the component to use as wrapper): use a customised wrapper
  // in all cases the 'component' can receive bindings specified by 'inputs' / 'outputs'
  public showDialog(options:IDialogOptions):IDialog {
    const wrapperComponent:Type<any> | false = options.wrapperComponent !== false? options.wrapperComponent || DefaultDialogWrapperComponent : NoDialogWrapperComponent;
    options.keyboard = options.keyboard || !options.buttons;
    options.backdrop = options.backdrop || 'static'; //default is static (clicking outside won't close the dialog)
    options.class = `modal-${options.size || DialogSize.LG}`;
    options.windowClass = options.windowClass || 'modal-dialog-scrollable'; // modal-dialog-scrollable makes only the content of dialog body scrollable

    const initialStateProps:string[] = [
      'title',
      'windowClass',
      'dialogClass',
      'closeButton',
      'buttons',
      'manualClose',
      'url',
      'message'
    ];
    const getInitialState:Function = () => {
      let initialState:IObject = _.pick(options, initialStateProps);
      initialState = util.copyAndExtend(options.initialState || {}, initialState);

      // options to pass to the wrapper component
      initialState.component = options.component;
      initialState.inputs = options.inputs;
      initialState.outputs = options.outputs;
      return initialState;
    };

    options.initialState = getInitialState();

    const modalOptionsOmitProps:string[] = util.copyAndExtend(initialStateProps, [
      'component',
      'inputs',
      'outputs',
      'size'
    ]);
    const modalOptions:ModalOptions = _.omit(options, modalOptionsOmitProps);
    const dialog:IDialog = this.modalService.show(wrapperComponent, modalOptions);

    this.modalDialogs.push(dialog);

    // trigger callback after the dialog is closed
    dialog?.onHide.subscribe(result => {
      util.remove(this.modalDialogs, dialog);
      if (options.callback) {
        options.callback(result);
      }
      dialog.onHide.unsubscribe();
    });

    return dialog;
  }

  public showGA1Dialog(title:string, url:string, dialogOptions?:IDialogOptions):IDialog {
    const options:IDialogOptions = {
      component: Ga1DialogComponent,
      title,
      closeButton: true,
      callback: null,
      keyboard: false,
      size: DialogSize.LG,
      windowClass: 'full iframe-ga1',
      inputs: {
        url
      },
      ...dialogOptions
    };
    return this.showDialog(options);
  }

  public showMessageDialog(title:string, message:string, buttons?:IDialogButton[], callback?:Function, closeCallback?:Function, size?:DialogSize, showCloseButton?:boolean, extraOptions?:IDialogOptions):IDialog {
    return this.showDialog({
      title,
      closeButton: showCloseButton,
      message,
      buttons,
      callback,
      closeCallback,
      manualClose: extraOptions?.manualClose,
      windowClass: extraOptions?.windowClass,
      dialogClass: extraOptions?.dialogClass,
      keyboard: extraOptions?.keyboard,
      size
    });
  }

  public showInvalidFormMessage(title:string, message:string, errors:(string|IFormErrors)[], callback?:DialogClickFn, buttons?:IDialogButton[], extraOptions?:IDialogOptions):IDialog {
    extraOptions = extraOptions || {};

    if (message) {
      message = `<span translate>${message}</span>`;
    } else {
      message = '';
    }

    if (errors.length) {
      if (_.isString(errors[ 0 ])) {
        //simple list of strings
        message += '<ul>';
        _.each(<string[]>errors, (error:string) => {
          message += `<li>${error}</li>`;
        });
        message += '</ul>';
      } else {
        _.each(errors, (errorSection:any) => {
          if(errorSection.title){
            message += `<h4 translate>${errorSection.title}</h4><ul>`;
          }
          _.each(errorSection.errors, error => {
            message += `<li>${error}</li>`;
          });
          message += '</ul>';
        });
      }
    }

    buttons = buttons || [
      {
        label: 'GA.BUTTONS.OK',
        className: 'btn-default',
        onClick: callback
      }
    ];

    return this.showMessageDialog(title, message, buttons, callback, extraOptions.closeCallback, DialogSize.MD, false, { dialogClass: MessageType.DANGER });
  }

  public showResponseErrorMessage(prefix:string, status?:string, response?:any):void {
    let message = `${prefix}: ${status}`;
    if (response?.message) {
      message += `<br>${response.message}`;
    }
    this.showErrorMessage(message);
  }

  public showErrorDialog(title:string, message:string):IDialog {
    return this.showSimpleDialog(title, message, MessageType.DANGER);
  }

  // display a list of error messages (e.g.: validation messages)
  public showErrorsDialog(errorMessages:string[]):IDialog {
    return this.showErrorDialog(this.translationService.instant('GA.VALIDATION.VALIDATION_FAILED'),
    `<ul>
				${_.map(errorMessages, (error:string) => {
        return `<li>${error}</li>`;
      }).join('')}
    </ul>`);
  }

  public showSuccessDialog(title:string, message:any):IDialog {
    return this.showSimpleDialog(title, message, MessageType.SUCCESS);
  }

  public showWarningDialog(title:string, message:any):IDialog {
    return this.showSimpleDialog(title, message, MessageType.WARNING);
  }

  public showEditingWarningDialog(onSave:Function, onDiscard?:Function, onContinue?:Function):void {
    this.showMessageDialog(
      'Leave page?',
      'You have not saved your changes. Should these be saved?',
      [
        {
          label: 'Save Changes',
          className: 'btn-success'
        },
        {
          label: 'Discard Changes',
          className: 'btn-danger'
        },
        {
          label: 'Continue Editing',
          className: 'btn-info'
        }
      ], (clickedButton:string) => {
        switch (clickedButton) {
          case 'Save Changes':
            console.debug('Save Changes');
            onSave();
            break;
          case 'Discard Changes':
            console.debug('Discard Changes');
            onDiscard();
            break;
          case 'Continue Editing':
            if (onContinue) {
              console.debug('Continue Editing');
              onContinue();
            }
            break;
          default:
            break;
        }
      }, undefined, DialogSize.MD);
  }

  private showSimpleDialog(title:string, message:string, dialogClass:string):IDialog {
    return this.showDialog({
      title,
      message,
      dialogClass,
      size: DialogSize.MD
    });
  }

  public unsavedChangesDialog(onSave:DialogClickFn, onDiscard:DialogClickFn, message?:string):IDialog {
    message = message || 'There are unsaved changes on the form. Do you want to save them before leaving?';

    const buttons:IDialogButton[] = [{
      label: 'Save Changes',
      className: 'btn-success',
      onClick: onSave
    }, {
      label: 'Discard Changes',
      className: 'btn-danger',
      onClick: onDiscard
    }, {
      label: 'Continue Editing',
      className: 'btn-primary',
      onClick: () => {
        unsavedChangesDialog.hide();
      }
    }];

    const unsavedChangesDialog:IDialog = this.showDialog({
      initialState: {
        title: 'Save changes before leaving?',
        message,
        buttons
      },
      size: DialogSize.MD
    });

    return unsavedChangesDialog;
  }

  /**
   * @ngdoc method
   * @name ga2.shared.message:messageService#closeAllDialogs
   * @methodOf ga2.shared.message:messageService
   *
   * @description Close all dialogs
   */
  public closeAllDialogs():void {
    _.each(this.modalDialogs, (dialog:IDialog) => {
      dialog.hide();
    });
  }
}